/**
 * BurnoutRiskPanel
 *
 * Adds burnout-risk visualisation on top of the existing PSA heatmap without
 * modifying any existing component internals.
 *
 * Architecture
 * ────────────
 * BurnoutRiskPanelWrapper
 *   ├─ BurnoutAlertBanners  (portaled into #burnout-banner-slot above the grid)
 *   └─ BurnoutSuggestionsPanel  (220 px panel alongside the grid, rendered in-flow)
 *
 * Both share state via BurnoutRiskContext.
 *
 * DOM-injection side-effects (badges, row tinting, trend arrows, what-if labels)
 * are managed in a single useEffect inside BurnoutSuggestionsPanel that watches
 * the Bryntum element via MutationObserver.
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  createContext,
  useContext,
} from 'react';
import ReactDOM from 'react-dom';
import { format, startOfWeek } from 'date-fns';
import { WhatIfConflictResolutionOverlay } from './WhatIfConflictResolutionOverlay';
import { Resource, Booking, WorkingHoursProfile, WhatIfScenario } from '@/types';
import {
  computeBurnoutRisk,
  generateSuggestions,
  detectComingFreeResources,
  generateUpcomingAvailabilitySuggestions,
  BurnoutRisk,
  RebalancingSuggestion,
  SharedSkill,
  ComingFreeResource,
  UpcomingAvailabilitySuggestion,
} from '@/lib/utils/burnout-risk';

// ─── Colour palette ───────────────────────────────────────────────────────────

const C = {
  criticalBg: 'rgba(254,226,226,0.50)',
  criticalBorder: '#ef4444',
  criticalBadgeBg: '#ef4444',
  highBg: 'rgba(255,251,235,0.50)',
  highBorder: '#f59e0b',
  highBadgeBg: '#f59e0b',
  tealAccent: '#0d9488',
  tealLight: 'rgba(13,148,136,0.08)',
  tealMid: 'rgba(13,148,136,0.15)',
  resolvedBg: 'rgba(220,252,231,0.50)',
  resolvedBorder: '#22c55e',
  mutedFg: 'var(--muted-foreground)',
  fg: 'var(--foreground)',
  panelBg: 'var(--background)',
  cardBg: 'var(--card)',
  border: 'var(--border)',
  mutedBg: 'var(--muted)',
};

// ─── Shared state types ───────────────────────────────────────────────────────

interface ResolvedSwap {
  atRiskName: string;
  candidateName: string;
  beforePct: number;
  afterPct: number;
}

interface BurnoutCtxValue {
  allRisks: BurnoutRisk[];
  allSuggestions: RebalancingSuggestion[];
  activeSuggestions: RebalancingSuggestion[];
  dismissedIds: Set<string>;
  resolvedIds: Set<string>;
  resolvedSwap: ResolvedSwap | null;
  atRiskCount: number;
  panelOpen: boolean;
  /** Coming-free raw data (one entry per resource) */
  comingFreeList: ComingFreeResource[];
  /** Enriched suggestions for the Upcoming availability section */
  upcomingAvailabilitySuggestions: UpcomingAvailabilitySuggestion[];
  /** Apply rebalance suggestion → hands off to calendar what-if */
  onApply: (s: RebalancingSuggestion) => void;
  /** Assign coming-free resource → hands off to calendar add-booking flow */
  onApplyFree: (cf: ComingFreeResource) => void;
  onDismiss: (id: string) => void;
  /** Called by ResourceCalendar after the user commits a what-if scenario */
  onCommitFromCalendar: (atRiskResourceId: string, atRiskName: string, candidateName: string, beforePct: number, afterPct: number) => void;
  onDismissResolvedSwap: () => void;
  onViewSuggestions: () => void;
  onClosePanel: () => void;
  /** Overlay: the suggestion currently shown in the full-screen conflict resolution overlay */
  overlaySuggestion: RebalancingSuggestion | null;
  /** Open the full-screen overlay for a specific suggestion */
  onOpenOverlay: (s: RebalancingSuggestion) => void;
  /** Close the overlay without navigating */
  onCloseOverlay: () => void;
}

const BurnoutCtx = createContext<BurnoutCtxValue | null>(null);

const useBurnout = () => {
  const ctx = useContext(BurnoutCtx);
  if (!ctx) throw new Error('useBurnout must be used inside BurnoutRiskProvider');
  return ctx;
};



// ─── Provider ────────────────────────────────────────────────────────────────

interface BurnoutRiskProviderProps {
  isActive: boolean;
  resources: Resource[];
  bookings: Booking[];
  globalWorkingProfile: WorkingHoursProfile;
  /** Called when Apply/Assign is clicked — navigates to calendar with scenario */
  onNavigateToCalendar: (scenario: WhatIfScenario) => void;
  children: React.ReactNode;
}

const BurnoutRiskProvider: React.FC<BurnoutRiskProviderProps> = ({
  isActive,
  resources,
  bookings,
  globalWorkingProfile,
  onNavigateToCalendar,
  children,
}) => {
  // ── Async risk computation — runs after render so it never blocks the heatmap ──
  const [allRisks, setAllRisks] = useState<BurnoutRisk[]>([]);
  const [allSuggestions, setAllSuggestions] = useState<RebalancingSuggestion[]>([]);
  const [comingFreeList, setComingFreeList] = useState<ComingFreeResource[]>([]);
  const [upcomingAvailabilitySuggestions, setUpcomingAvailabilitySuggestions] = useState<UpcomingAvailabilitySuggestion[]>([]);

  // Stable key so the effect only fires when the actual data identity changes,
  // not on every parent re-render.
  const resourcesRef = useRef(resources);
  const bookingsRef = useRef(bookings);
  const profileRef = useRef(globalWorkingProfile);
  resourcesRef.current = resources;
  bookingsRef.current = bookings;
  profileRef.current = globalWorkingProfile;

  const resourceIds = resources.map((r) => r.id).join(',');
  const bookingCount = bookings.length;

  useEffect(() => {
    if (!isActive) {
      setAllRisks([]);
      setAllSuggestions([]);
      setComingFreeList([]);
      setUpcomingAvailabilitySuggestions([]);
      return;
    }

    // Defer heavy computation so the heatmap renders first
    const tid = window.setTimeout(() => {
      const risks = resourcesRef.current
        .filter((r) => r.id !== 'unallocated')
        .map((r) =>
          computeBurnoutRisk(r, bookingsRef.current, profileRef.current, new Date(), 8)
        )
        .filter((risk) => risk.level !== 'none');

      setAllRisks(risks);

      // Second deferred tick so suggestions don't pile onto the same frame as risks
      window.setTimeout(() => {
        if (risks.length > 0) {
          setAllSuggestions(
            generateSuggestions(risks, resourcesRef.current, bookingsRef.current, profileRef.current)
          );
        } else {
          setAllSuggestions([]);
        }

        // Coming-free detection — runs regardless of burnout risks
        const free = detectComingFreeResources(
          resourcesRef.current,
          bookingsRef.current,
          profileRef.current,
          new Date(),
          8
        );
        setComingFreeList(free);

        if (free.length > 0) {
          setUpcomingAvailabilitySuggestions(
            generateUpcomingAvailabilitySuggestions(
              free,
              risks,
              resourcesRef.current,
              bookingsRef.current,
              profileRef.current
            )
          );
        } else {
          setUpcomingAvailabilitySuggestions([]);
        }
      }, 0);
    }, 0);

    return () => window.clearTimeout(tid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, resourceIds, bookingCount]);

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  const [resolvedSwap, setResolvedSwap] = useState<ResolvedSwap | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  // ── Full-screen overlay state ─────────────────────────────────────────────
  const [overlaySuggestion, setOverlaySuggestion] = useState<RebalancingSuggestion | null>(null);

  const activeSuggestions = useMemo(
    () =>
      allSuggestions.filter(
        (s) =>
          !dismissedIds.has(s.atRiskResourceId) &&
          !resolvedIds.has(s.atRiskResourceId)
      ),
    [allSuggestions, dismissedIds, resolvedIds]
  );

  const atRiskCount = activeSuggestions.length;

  // ── Apply rebalance suggestion → navigate calendar to what-if mode ───────
  const onApply = useCallback(
    (s: RebalancingSuggestion) => {
      const targetWeekStart =
        s.affectedWeekStarts.length > 0
          ? s.affectedWeekStarts[0]
          : startOfWeek(new Date(), { weekStartsOn: 1 });

      // Find the actual booking IDs for this resource+project so BryntumSchedulerTimeline
      // can highlight/ghost the exact tiles rather than guessing by CSS selector.
      // IMPORTANT: Only match bookings within the target week — Sarah (and others) may
      // have the same project name across many weeks, and we only want to ghost the
      // specific booking being proposed for reassignment, not every occurrence.
      // Widen the filter window by 1 day in each direction so bookings that
      // fall on a Sunday (i.e. the day *before* the Monday week start that
      // startOfWeek returns) are still captured correctly.
      const filterFrom = new Date(targetWeekStart);
      filterFrom.setDate(filterFrom.getDate() - 1);
      const targetWeekEnd = new Date(targetWeekStart);
      targetWeekEnd.setDate(targetWeekEnd.getDate() + 7);
      const atRiskBookings = bookings.filter(
        (b) =>
          String(b.resourceId) === String(s.atRiskResourceId) &&
          (b.projectName === s.projectName || b.projectId === s.projectName) &&
          new Date(b.startTime) >= filterFrom &&
          new Date(b.startTime) < targetWeekEnd
      );

      const atRiskBookingIds = atRiskBookings.map((b) => String(b.id));

      // Carry the precise start/end dates so the ghost tile is sized to match
      // the actual booking span rather than defaulting to a full Mon–Fri week.
      // Booking uses startTime/endTime (full ISO datetime strings).
      const atRiskBookingDates = atRiskBookings
        .map((b) => ({
          startDate: new Date(b.startTime),
          endDate: new Date(b.endTime),
        }))
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

      onNavigateToCalendar({
        type: 'rebalance',
        targetWeekStart,
        atRiskResourceId: s.atRiskResourceId,
        atRiskResourceName: s.atRiskResourceName,
        candidateResourceId: s.candidateId,
        candidateResourceName: s.candidateName,
        candidateResourceRole: s.candidateRole,
        projectName: s.projectName,
        affectedWeekStarts: s.affectedWeekStarts,
        atRiskBookingIds: atRiskBookingIds.length > 0 ? atRiskBookingIds : undefined,
        atRiskBookingDates: atRiskBookingDates.length > 0 ? atRiskBookingDates : undefined,
      });
    },
    [onNavigateToCalendar, bookings]
  );

  // ── Assign coming-free → navigate calendar to add-booking flow ───────────
  const onApplyFree = useCallback(
    (cf: ComingFreeResource) => {
      onNavigateToCalendar({
        type: 'assign-free',
        targetWeekStart: cf.freeFromWeekStart,
        freeResourceId: cf.resourceId,
        freeResourceName: cf.resourceName,
        freeResourceRole: cf.resourceRole,
        freeFromWeek: cf.freeFromWeek,
        availableCapacityPct: cf.availableCapacityPct,
        endingProjectName: cf.endingProjectName ?? undefined,
      });
    },
    [onNavigateToCalendar]
  );

  const onDismiss = useCallback(
    (id: string) => setDismissedIds((p) => new Set([...p, id])),
    []
  );

  /** Called by ResourceCalendar toolbar "Commit changes" after user confirms */
  const onCommitFromCalendar = useCallback(
    (
      atRiskResourceId: string,
      atRiskName: string,
      candidateName: string,
      beforePct: number,
      afterPct: number
    ) => {
      setResolvedIds((p) => new Set([...p, atRiskResourceId]));
      setResolvedSwap({ atRiskName, candidateName, beforePct, afterPct });
    },
    []
  );
  const onDismissResolvedSwap = useCallback(() => setResolvedSwap(null), []);

  // Open overlay directly with the most critical suggestion — skip the drawer
  const onViewSuggestions = useCallback(() => {
    const suggestions = allSuggestions.filter(
      (s) => !dismissedIds.has(s.atRiskResourceId) && !resolvedIds.has(s.atRiskResourceId)
    );
    const first = suggestions[0];
    if (first) setOverlaySuggestion(first);
  }, [allSuggestions, dismissedIds, resolvedIds]);

  const onClosePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);
  // ── Overlay callbacks ────────────────────────────────────────────────────
  const onOpenOverlay = useCallback((s: RebalancingSuggestion) => {
    setOverlaySuggestion(s);
  }, []);
  const onCloseOverlay = useCallback(() => {
    setOverlaySuggestion(null);
  }, []);

  return (
    <BurnoutCtx.Provider
      value={{
        allRisks,
        allSuggestions,
        activeSuggestions,
        dismissedIds,
        resolvedIds,
        resolvedSwap,
        atRiskCount,
        panelOpen,
        comingFreeList,
        upcomingAvailabilitySuggestions,
        onApply,
        onApplyFree,
        onDismiss,
        onCommitFromCalendar,
        onDismissResolvedSwap,
        onViewSuggestions,
        onClosePanel,
        overlaySuggestion,
        onOpenOverlay,
        onCloseOverlay,
      }}
    >
      {children}
    </BurnoutCtx.Provider>
  );
};

// ─── Utility bar component ────────────────────────────────────────────────────

const UtilBar: React.FC<{
  label: string;
  before: number;
  after: number;
  isAtRisk?: boolean;
}> = ({ label, before, after, isAtRisk }) => {
  const clamp = (v: number) => Math.min(150, Math.max(0, v));
  const bw = `${Math.round((clamp(before) / 150) * 100)}%`;
  const aw = `${Math.round((clamp(after) / 150) * 100)}%`;
  const ac = after > 100 ? C.criticalBorder : after > 85 ? '#f59e0b' : '#22c55e';
  const bc = isAtRisk ? (before > 110 ? C.criticalBorder : '#f59e0b') : '#64748b';

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: C.fg, marginBottom: 4 }}>{label}</div>
      {[
        { tag: 'before', w: bw, col: bc, val: before },
        { tag: 'after', w: aw, col: ac, val: after },
      ].map(({ tag, w, col, val }) => (
        <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
          <span style={{ fontSize: 9, color: C.mutedFg, width: 30 }}>{tag}</span>
          <div style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: 'var(--muted)', overflow: 'hidden' }}>
            <div style={{ width: w, height: '100%', backgroundColor: col, borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color: col, width: 34, textAlign: 'right' }}>
            {Math.round(val)}%
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Alert banner component ───────────────────────────────────────────────────

const AlertBanners: React.FC = () => {
  const { atRiskCount, resolvedSwap, onViewSuggestions, onDismissResolvedSwap } = useBurnout();

  return (
    <>
      {/* ── Resolved success banner ─────────────────────────────────────── */}
      {resolvedSwap ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '7px 16px',
          background: 'linear-gradient(90deg, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0.03) 100%)',
          borderBottom: '1px solid rgba(34,197,94,0.30)',
          borderLeft: '3px solid #22c55e',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {/* Icon circle */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              backgroundColor: '#22c55e', color: '#fff', fontSize: 12, fontWeight: 700,
            }}>
              ✓
            </span>
            {/* Main text */}
            <span style={{ fontSize: 12, color: '#14532d', lineHeight: 1.4, minWidth: 0 }}>
              <span style={{ fontWeight: 700, color: '#15803d' }}>Rebalancing committed</span>
              <span style={{ color: '#166534' }}>
                {' — '}<strong>{resolvedSwap.atRiskName}</strong>{' redistributed to '}
                <strong>{resolvedSwap.candidateName}</strong>
              </span>
            </span>
            {/* Metric pill */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
              padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
              backgroundColor: 'rgba(34,197,94,0.15)', color: '#15803d',
              border: '1px solid rgba(34,197,94,0.30)',
            }}>
              {resolvedSwap.beforePct}%
              <span style={{ opacity: 0.5 }}>→</span>
              {resolvedSwap.afterPct}%
            </span>
            {/* Residual risk note */}
            {atRiskCount > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '2px 8px', borderRadius: 20, flexShrink: 0,
                fontSize: 10, fontWeight: 600,
                backgroundColor: 'rgba(245,158,11,0.12)', color: '#92400e',
                border: '1px solid rgba(245,158,11,0.25)',
              }}>
                {atRiskCount} still at risk
              </span>
            )}
          </div>
          {/* Dismiss button */}
          <button
            onClick={onDismissResolvedSwap}
            title="Dismiss"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)',
              cursor: 'pointer', color: '#15803d', fontSize: 11, fontWeight: 700,
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(34,197,94,0.22)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(34,197,94,0.12)'; }}
          >
            ✕
          </button>
        </div>

      ) : atRiskCount > 0 ? (

        /* ── At-risk warning banner ──────────────────────────────────────── */
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '7px 16px',
          background: 'linear-gradient(90deg, rgba(245,158,11,0.09) 0%, rgba(245,158,11,0.03) 100%)',
          borderBottom: '1px solid rgba(245,158,11,0.30)',
          borderLeft: '3px solid #f59e0b',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Animated pulse dot */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              backgroundColor: 'rgba(245,158,11,0.15)', border: '1.5px solid #f59e0b',
              fontSize: 11,
            }}>
              ⚠
            </span>
            {/* Count pill */}
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '2px 9px', borderRadius: 20, flexShrink: 0,
              fontSize: 11, fontWeight: 700,
              backgroundColor: '#f59e0b', color: '#fff',
              letterSpacing: '0.01em',
            }}>
              {atRiskCount} {atRiskCount === 1 ? 'resource' : 'resources'}
            </span>
            {/* Message */}
            <span style={{
              fontSize: 12, fontWeight: 500, color: '#78350f', lineHeight: 1.4,
            }}>
              trending toward burnout — rebalancing suggested
            </span>
          </div>

          {/* CTA button */}
          <button
            onClick={onViewSuggestions}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 12px', borderRadius: 20, flexShrink: 0,
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              backgroundColor: C.tealAccent, color: '#fff',
              border: 'none', letterSpacing: '0.01em',
              transition: 'opacity 0.15s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
          >
            View suggestions
            <span style={{ fontSize: 13, lineHeight: 1, marginTop: 0.5 }}>›</span>
          </button>
        </div>

      ) : null}
    </>
  );
};

// ─── Suggestion card — compact row ───────────────────────────────────────────
// Each card is a single tight row: accent left-bar, name+util badge top line,
// candidate + actions bottom line. Fits ~5–6 cards in the visible panel.

const SuggestionCard: React.FC<{ suggestion: RebalancingSuggestion }> = ({ suggestion: s }) => {
  const { onApply, onDismiss, onOpenOverlay } = useBurnout();
  const isCritical = s.level === 'critical';
  const accentColor = isCritical ? C.criticalBorder : C.highBorder;
  const badgeColor  = isCritical ? '#b91c1c' : '#92400e';
  const badgeBg     = isCritical ? 'rgba(239,68,68,0.10)' : 'rgba(245,158,11,0.10)';

  return (
    <div style={{
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: 6,
      marginBottom: 6,
      backgroundColor: C.cardBg,
      border: `1px solid var(--border)`,
      borderLeftColor: accentColor,
      borderLeftWidth: 3,
      overflow: 'hidden',
    }}>
      <div style={{ padding: '8px 10px' }}>
        {/* Row 1 — name + utilisation badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 5 }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: C.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {s.atRiskResourceName}
          </span>
          <span style={{
            flexShrink: 0, fontSize: 11, fontWeight: 800,
            color: badgeColor, background: badgeBg,
            borderRadius: 4, padding: '1px 6px', letterSpacing: '-0.01em',
          }}>
            {s.peakUtilisation}%
          </span>
        </div>

        {/* Row 2 — project + week (muted, single line) */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4, marginBottom: 7,
          fontSize: 10.5, color: C.mutedFg, overflow: 'hidden',
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {s.projectName}
          </span>
          {s.affectedWeekStarts.length > 0 && (
            <span style={{ flexShrink: 0, fontWeight: 600, color: C.fg, fontSize: 10 }}>
              {format(s.affectedWeekStarts[0], 'd MMM')}
            </span>
          )}
        </div>

        {/* Row 3 — candidate chip + action buttons inline */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Candidate chip — expanded with skill coverage */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 5, flex: 1, minWidth: 0,
            padding: '5px 7px', borderRadius: 5,
            backgroundColor: C.tealLight, border: `1px solid ${C.tealMid}`,
          }}>
            {/* Avatar */}
            <span style={{
              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
              backgroundColor: C.tealAccent, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff',
              marginTop: 1,
            }}>
              {s.candidateName.charAt(0)}
            </span>

            <div style={{ minWidth: 0, flex: 1 }}>
              {/* Name row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.tealAccent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.candidateName}
                </span>
                {s.sameRole && (
                  <span style={{
                    fontSize: 8, padding: '1px 4px', borderRadius: 3, flexShrink: 0,
                    backgroundColor: 'rgba(13,148,136,0.15)', color: C.tealAccent, fontWeight: 700,
                  }}>role ✓</span>
                )}
                {/* Match score badge */}
                <span style={{
                  marginLeft: 'auto', flexShrink: 0, fontSize: 9, fontWeight: 800,
                  padding: '1px 5px', borderRadius: 10,
                  backgroundColor: s.candidateScore >= 80 ? 'rgba(34,197,94,0.12)' : s.candidateScore >= 55 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.10)',
                  color: s.candidateScore >= 80 ? '#15803d' : s.candidateScore >= 55 ? '#92400e' : '#b91c1c',
                  border: `1px solid ${s.candidateScore >= 80 ? 'rgba(34,197,94,0.30)' : s.candidateScore >= 55 ? 'rgba(245,158,11,0.30)' : 'rgba(239,68,68,0.25)'}`,
                }}>
                  {s.candidateScore}% match
                </span>
              </div>

              {/* Util + skill coverage row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: s.sharedSkills.length > 0 ? 4 : 0 }}>
                <span style={{ fontSize: 9.5, color: C.mutedFg }}>
                  avg {s.candidateAvgUtilisation}% util
                </span>
                {s.totalAtRiskSkills > 0 && (
                  <span style={{
                    fontSize: 9, color: s.sharedSkills.length === s.totalAtRiskSkills ? '#15803d' : s.missingSkills.length > 0 ? '#92400e' : C.mutedFg,
                    fontWeight: 600,
                  }}>
                    · {s.sharedSkills.length}/{s.totalAtRiskSkills} skills
                  </span>
                )}
              </div>

              {/* Skill pills */}
              {s.sharedSkills.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: s.missingSkills.length > 0 ? 3 : 0 }}>
                  {s.sharedSkills.map((sk: SharedSkill) => (
                    <span
                      key={sk.name}
                      title={`${sk.name} — candidate: ${sk.rating}/5 · required: ${sk.atRiskRating}/5`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        padding: '1px 5px', borderRadius: 4, fontSize: 8.5,
                        backgroundColor: 'rgba(13,148,136,0.10)',
                        border: '1px solid rgba(13,148,136,0.20)',
                        color: C.tealAccent, fontWeight: 600,
                        cursor: 'default',
                      }}
                    >
                      {sk.name}
                      {/* Proficiency dots — one dot per 2 rating points */}
                      <span style={{ display: 'inline-flex', gap: 1.5 }}>
                        {[1,2,3,4,5].map((i) => (
                          <span key={i} style={{
                            width: 4, height: 4, borderRadius: '50%',
                            backgroundColor: i <= sk.rating ? C.tealAccent : 'rgba(13,148,136,0.20)',
                            display: 'inline-block',
                          }} />
                        ))}
                      </span>
                    </span>
                  ))}
                </div>
              )}

              {/* Missing skills warning */}
              {s.missingSkills.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {s.missingSkills.slice(0, 3).map((name: string) => (
                    <span
                      key={name}
                      title={`${name} — not recorded for ${s.candidateName}`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 2,
                        padding: '1px 5px', borderRadius: 4, fontSize: 8.5,
                        backgroundColor: 'rgba(239,68,68,0.06)',
                        border: '1px solid rgba(239,68,68,0.18)',
                        color: '#b91c1c', fontWeight: 600,
                        cursor: 'default',
                      }}
                    >
                      <span style={{ fontSize: 8 }}>✕</span> {name}
                    </span>
                  ))}
                  {s.missingSkills.length > 3 && (
                    <span style={{ fontSize: 8.5, color: C.mutedFg, alignSelf: 'center' }}>
                      +{s.missingSkills.length - 3} more missing
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions — compact */}
          <button
            onClick={() => onOpenOverlay(s)}
            title="Open conflict resolution"
            style={{
              flexShrink: 0, padding: '5px 9px', fontSize: 11, fontWeight: 700,
              borderRadius: 5, border: 'none',
              backgroundColor: C.tealAccent, color: '#fff',
              cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.82'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
          >
            Preview
          </button>
          <button
            onClick={() => onDismiss(s.atRiskResourceId)}
            title="Dismiss this alert"
            style={{
              flexShrink: 0, width: 24, height: 24, fontSize: 13, fontWeight: 500,
              borderRadius: 5, border: '1px solid var(--border)', color: C.mutedFg,
              backgroundColor: 'transparent', cursor: 'pointer', lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--muted)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};



// ─── Resolved card ────────────────────────────────────────────────────────────

const ResolvedCard: React.FC<{ swap: ResolvedSwap }> = ({ swap }) => (
  <div style={{
    border: '1px solid #22c55e',
    borderLeftWidth: 4,
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(34,197,94,0.12)',
  }}>
    <div style={{
      padding: '10px 14px',
      background: 'linear-gradient(135deg, rgba(220,252,231,0.6) 0%, rgba(220,252,231,0.2) 100%)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
          backgroundColor: '#22c55e', color: '#fff', fontSize: 11, fontWeight: 800,
        }}>✓</span>
        <span style={{ fontWeight: 700, color: '#15803d', fontSize: 13 }}>
          Resolved — {swap.atRiskName}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#16a34a', marginBottom: 4 }}>
        Redistributed to <strong>{swap.candidateName}</strong>
      </div>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700,
        backgroundColor: 'rgba(34,197,94,0.15)', color: '#15803d',
        border: '1px solid rgba(34,197,94,0.3)',
      }}>
        {swap.beforePct}% <span style={{ opacity: 0.5 }}>→</span> {swap.afterPct}%
      </div>
    </div>
  </div>
);

// ─── DOM injection hook ───────────────────────────────────────────────────────
// No MutationObserver — Bryntum's virtual renderer fires thousands of mutations
// per second which causes a feedback loop and freezes the page.
// Instead we inject once after Bryntum has rendered (retry up to 12×, 250 ms
// apart) and re-inject only when our own data changes.

function useBurnoutDomInjection(
  schedulerElRef: React.RefObject<HTMLDivElement | null>,
  isActive: boolean
) {
  const { allRisks, resolvedIds, comingFreeList } = useBurnout();

  // Keep latest values in refs so the inject function never captures stale closures
  const allRisksRef = useRef(allRisks);
  const resolvedIdsRef = useRef(resolvedIds);
  const comingFreeRef = useRef(comingFreeList);
  allRisksRef.current = allRisks;
  resolvedIdsRef.current = resolvedIds;
  comingFreeRef.current = comingFreeList;

  // Stable inject function — reads from refs so it never needs to be recreated
  const injectRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    injectRef.current = () => {
      const el = schedulerElRef.current;
      if (!el || !isActive) return;

      const risks = allRisksRef.current;
      const resolved = resolvedIdsRef.current;

      // ── Row tinting ────────────────────────────────────────────────────
      el.querySelectorAll<HTMLElement>('[data-bt]').forEach((e) => {
        e.style.removeProperty('background-color');
        e.removeAttribute('data-bt');
      });
      risks.forEach((risk) => {
        const tint = resolved.has(risk.resourceId)
          ? C.resolvedBg
          : risk.level === 'critical'
          ? C.criticalBg
          : C.highBg;
        el.querySelectorAll<HTMLElement>(
          `.b-grid-row[data-id="${risk.resourceId}"]`
        ).forEach((row) => {
          row.style.backgroundColor = tint;
          row.setAttribute('data-bt', '1');
        });
      });

      // ── Risk badges (burnout or coming-free, mutually exclusive per resource) ──
      el.querySelectorAll<HTMLElement>('[data-bb]').forEach((e) => e.remove());
      const comingFreeById = new Map<string, ComingFreeResource>();
      comingFreeRef.current.forEach((cf) => comingFreeById.set(cf.resourceId, cf));

      // Collect all resource IDs that need a badge (burnout OR coming-free)
      const allBadgeIds = new Set<string>([
        ...risks.map((r) => r.resourceId),
        ...comingFreeRef.current.map((cf) => cf.resourceId),
      ]);

      allBadgeIds.forEach((resourceId) => {
        const row = el.querySelector<HTMLElement>(
          `.b-grid-row[data-id="${resourceId}"]`
        );
        if (!row) return;
        const cell = row.querySelector<HTMLElement>('.b-grid-cell');
        if (!cell) return;

        const badge = document.createElement('span');
        badge.setAttribute('data-bb', resourceId);

        const cf = comingFreeById.get(resourceId);
        const risk = risks.find((r) => r.resourceId === resourceId);
        const isResolved = resolved.has(resourceId);

        // Coming-free takes visual priority — replaces burnout badge
        if (cf) {
          badge.textContent = `↓ free W${cf.freeFromWeek}`;
          badge.title = `Utilisation drops from ${cf.utilisationBefore}% → ${cf.utilisationAfter}% at W${cf.freeFromWeek}${cf.endingProjectName ? ` · ${cf.endingProjectName} ending` : ''}`;
          Object.assign(badge.style, {
            display: 'inline-flex', alignItems: 'center',
            fontSize: '9px', fontWeight: '700', padding: '1px 4px',
            borderRadius: '4px', color: '#fff', marginLeft: '4px',
            flexShrink: '0', letterSpacing: '0.03em', verticalAlign: 'middle',
            backgroundColor: C.tealAccent,
          });
        } else if (risk) {
          badge.textContent = isResolved
            ? '✓ ok'
            : risk.level === 'critical'
            ? '↑ crit'
            : '↑ high';
          badge.title = isResolved ? 'Risk resolved' : risk.reason;
          Object.assign(badge.style, {
            display: 'inline-flex', alignItems: 'center',
            fontSize: '9px', fontWeight: '700', padding: '1px 4px',
            borderRadius: '4px', color: '#fff', marginLeft: '4px',
            flexShrink: '0', letterSpacing: '0.03em', verticalAlign: 'middle',
            backgroundColor: isResolved
              ? '#22c55e'
              : risk.level === 'critical'
              ? C.criticalBadgeBg
              : C.highBadgeBg,
          });
        } else {
          return; // Nothing to render
        }

        cell.style.cssText += ';display:flex;align-items:center;overflow:visible';
        cell.appendChild(badge);
      });

      // ── Trend arrows on heatmap cells ──────────────────────────────────
      el.querySelectorAll<HTMLElement>('[data-btr]').forEach((e) => e.remove());
      risks.forEach((risk) => {
        if (risk.trendWeekStarts.length === 0) return;
        el.querySelectorAll<HTMLElement>(
          `.b-grid-row[data-id="${risk.resourceId}"] .b-heatmap-event`
        ).forEach((cell) => {
          const pct = parseInt((cell.textContent ?? '').replace(/[^0-9]/g, ''), 10);
          if (isNaN(pct) || pct < 85 || cell.querySelector('[data-btr]')) return;
          const arrow = document.createElement('span');
          arrow.setAttribute('data-btr', '1');
          arrow.textContent = '↑';
          arrow.title = risk.reason;
          Object.assign(arrow.style, {
            position: 'absolute', top: '2px', right: '3px',
            fontSize: '9px', fontWeight: '700', color: '#ef4444',
            lineHeight: '1', pointerEvents: 'none', zIndex: '30',
          });
          cell.style.position = 'relative';
          cell.appendChild(arrow);
        });
      });

    };
  });

  // Run inject after Bryntum has had time to render. Retry up to 12× every
  // 250 ms to handle slow initial renders. Never observes the DOM.
  useEffect(() => {
    if (!isActive) return;

    let attempts = 0;
    const MAX = 12;
    const INTERVAL = 250;

    const tryInject = () => {
      const el = schedulerElRef.current;
      // Wait until Bryntum has actually rendered some rows
      if (el && el.querySelector('.b-grid-row')) {
        injectRef.current?.();
        return;
      }
      attempts++;
      if (attempts < MAX) {
        tid = window.setTimeout(tryInject, INTERVAL);
      }
    };

    let tid = window.setTimeout(tryInject, 300);

    return () => {
      window.clearTimeout(tid);
      // Cleanup injected elements
      const el = schedulerElRef.current;
      if (!el) return;
      el.querySelectorAll('[data-bt]').forEach((e) => {
        (e as HTMLElement).style.removeProperty('background-color');
        e.removeAttribute('data-bt');
      });
      el.querySelectorAll('[data-bb],[data-btr]').forEach((e) => e.remove());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Re-inject whenever risk data, coming-free data or what-if state changes
  const riskKey = allRisks.map((r) => `${r.resourceId}:${r.level}`).join(',');
  const resolvedKey = [...resolvedIds].join(',');
  const comingFreeKey = comingFreeList.map((cf) => `${cf.resourceId}:${cf.freeFromWeek}`).join(',');

  useEffect(() => {
    if (!isActive) return;
    // Small delay so Bryntum has painted any layout changes first
    const tid = window.setTimeout(() => injectRef.current?.(), 80);
    return () => window.clearTimeout(tid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, riskKey, resolvedKey, comingFreeKey]);
}

// ─── Upcoming availability card — compact row ─────────────────────────────────
// Single-row card: name + free-week badge top, role + ending project + assign bottom.

const UpcomingAvailabilityCard: React.FC<{ suggestion: UpcomingAvailabilitySuggestion }> = ({
  suggestion: { comingFree: cf, matches },
}) => {
  const { onApplyFree } = useBurnout();
  const topMatch = matches[0];

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderLeftWidth: 3,
      borderLeftColor: C.tealAccent,
      borderRadius: 6,
      marginBottom: 6,
      backgroundColor: C.cardBg,
      overflow: 'hidden',
    }}>
      <div style={{ padding: '8px 10px' }}>
        {/* Row 1 — name + free badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: C.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cf.resourceName}
          </span>
          <span style={{
            flexShrink: 0, fontSize: 10, fontWeight: 700,
            padding: '1px 6px', borderRadius: 4,
            backgroundColor: C.tealLight, color: C.tealAccent,
            border: `1px solid ${C.tealMid}`,
            whiteSpace: 'nowrap',
          }}>
            ↓ free W{cf.freeFromWeek}
          </span>
        </div>

        {/* Row 2 — role + capacity */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6,
          fontSize: 10.5, color: C.mutedFg,
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {cf.resourceRole}
            {cf.endingProjectName && (
              <> · <span style={{ color: C.fg, fontWeight: 500 }}>{cf.endingProjectName}</span></>
            )}
          </span>
          <span style={{ flexShrink: 0, fontWeight: 600, color: C.tealAccent, fontSize: 10 }}>
            ~{cf.availableCapacityPct}%
          </span>
        </div>

        {/* Row 3 — top match + assign button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {topMatch ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 5, flex: 1, minWidth: 0,
              padding: '4px 7px', borderRadius: 5,
              backgroundColor: topMatch.matchType === 'at-risk'
                ? 'rgba(239,68,68,0.06)'
                : 'var(--muted)',
              border: `1px solid ${topMatch.matchType === 'at-risk' ? 'rgba(239,68,68,0.15)' : 'var(--border)'}`,
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                backgroundColor: topMatch.matchType === 'at-risk' ? C.criticalBorder : '#94a3b8',
              }} />
              <span style={{
                fontSize: 11, fontWeight: 600, color: C.fg,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {topMatch.resourceName}
              </span>
              {matches.length > 1 && (
                <span style={{ fontSize: 10, color: C.mutedFg, flexShrink: 0 }}>
                  +{matches.length - 1}
                </span>
              )}
            </div>
          ) : (
            <div style={{ flex: 1 }} />
          )}

          <button
            onClick={() => onApplyFree(cf)}
            title="Preview assignment"
            style={{
              flexShrink: 0, padding: '5px 9px', fontSize: 11, fontWeight: 700,
              borderRadius: 5, border: 'none',
              backgroundColor: C.tealAccent, color: '#fff',
              cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.82'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Suggestions panel ────────────────────────────────────────────────────────

interface BurnoutSuggestionsPanelProps {
  schedulerElRef: React.RefObject<HTMLDivElement | null>;
  isActive: boolean;
}

const BurnoutSuggestionsPanel: React.FC<BurnoutSuggestionsPanelProps> = ({
  schedulerElRef,
  isActive,
}) => {
  const {
    allSuggestions,
    activeSuggestions,
    resolvedSwap,
    panelOpen,
    onClosePanel,
    upcomingAvailabilitySuggestions,
  } = useBurnout();
  useBurnoutDomInjection(schedulerElRef, isActive);

  // Auto-clear the resolved card after 4 seconds so it doesn't keep the panel open
  const [showResolved, setShowResolved] = React.useState(false);
  React.useEffect(() => {
    if (resolvedSwap) {
      setShowResolved(true);
      const tid = window.setTimeout(() => setShowResolved(false), 4000);
      return () => window.clearTimeout(tid);
    } else {
      setShowResolved(false);
    }
  }, [resolvedSwap]);

  // Only show when explicitly opened via "View suggestions ›"
  if (!panelOpen) return null;

  const subtitleText = [
    activeSuggestions.length > 0 && `${activeSuggestions.length} at-risk`,
    upcomingAvailabilitySuggestions.length > 0 && `${upcomingAvailabilitySuggestions.length} coming free`,
  ]
    .filter(Boolean)
    .join(' · ') || 'System generated';

  return (
    <div
      id="burnout-suggestions-panel"
      style={{
        width: 420,
        flexShrink: 0,
        borderLeft: `1px solid ${C.border}`,
        backgroundColor: C.panelBg,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '-2px 0 12px rgba(0,0,0,0.06)',
      }}
    >
      {/* ── Panel header ──────────────────────────────────────────────── */}
      <div style={{
        padding: '10px 12px 8px',
        borderBottom: `1px solid ${C.border}`,
        flexShrink: 0,
        background: `linear-gradient(to bottom, var(--card), var(--background))`,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: C.fg, lineHeight: 1.2 }}>
              Rebalancing suggestions
            </span>
          </div>
          {/* Close button */}
          <button
            onClick={onClosePanel}
            title="Close panel"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 24, height: 24, borderRadius: 6, flexShrink: 0,
              background: 'var(--muted)', border: '1px solid var(--border)',
              cursor: 'pointer', color: C.mutedFg, fontSize: 14, lineHeight: 1,
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--muted)'; }}
          >
            ×
          </button>
        </div>
        {/* Subtitle row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {activeSuggestions.length > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
              backgroundColor: 'rgba(239,68,68,0.1)', color: '#b91c1c',
              border: '1px solid rgba(239,68,68,0.2)',
            }}>
              {activeSuggestions.length} at-risk
            </span>
          )}
          {upcomingAvailabilitySuggestions.length > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
              backgroundColor: C.tealLight, color: C.tealAccent,
              border: `1px solid ${C.tealMid}`,
            }}>
              {upcomingAvailabilitySuggestions.length} coming free
            </span>
          )}
          {activeSuggestions.length === 0 && upcomingAvailabilitySuggestions.length === 0 && (
            <span style={{ fontSize: 12, color: C.mutedFg }}>{subtitleText}</span>
          )}
        </div>
      </div>

      {/* ── Panel body ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px 12px' }}>
        <>
          {/* ── Burnout alerts section ────────────────────────────── */}
          {showResolved && resolvedSwap && <ResolvedCard swap={resolvedSwap} />}

          {activeSuggestions.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '24px 12px',
              fontSize: 12, color: C.mutedFg,
              borderRadius: 8, border: '1px dashed var(--border)',
              backgroundColor: C.mutedBg,
            }}>
              {allSuggestions.length === 0
                ? '✓ No burnout risks detected in the current view.'
                : '✓ All alerts resolved or dismissed.'}
            </div>
          ) : (
            <>
              {/* At-risk section label */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
              }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: '#b91c1c',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>
                  🔴 At risk
                </span>
                <div style={{ flex: 1, height: 1, backgroundColor: 'var(--border)' }} />
              </div>
              {activeSuggestions.map((s) => (
                <SuggestionCard key={s.atRiskResourceId} suggestion={s} />
              ))}
            </>
          )}

          {/* ── Upcoming availability section ─────────────────────── */}
          {upcomingAvailabilitySuggestions.length > 0 && (
            <>
              {/* Section divider */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                margin: '10px 0 6px',
              }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: C.tealAccent,
                  textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0,
                }}>
                  🟢 Upcoming availability
                </span>
                <div style={{ flex: 1, height: 1, backgroundColor: 'var(--border)' }} />
              </div>

              {upcomingAvailabilitySuggestions.map((s) => (
                <UpcomingAvailabilityCard
                  key={s.comingFree.resourceId}
                  suggestion={s}
                />
              ))}
            </>
          )}
        </>
      </div>
    </div>
  );
};

// ─── Banner portal ────────────────────────────────────────────────────────────

const BannerPortal: React.FC = () => {
  const [slot, setSlot] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    // Poll for the slot element — it lives outside the React subtree of this
    // component so it may not exist on first render.
    const el = document.getElementById('burnout-banner-slot');
    if (el) { setSlot(el); return; }
    const id = window.setInterval(() => {
      const found = document.getElementById('burnout-banner-slot');
      if (found) { setSlot(found); window.clearInterval(id); }
    }, 50);
    return () => window.clearInterval(id);
  }, []);

  // Only render into the slot — never inline (that puts banners over the grid)
  if (!slot) return null;
  return ReactDOM.createPortal(<AlertBanners />, slot);
};

// ─── Overlay portal ───────────────────────────────────────────────────────────
// Rendered inside BurnoutRiskProvider so it can access BurnoutCtx.
// Uses React.createPortal to attach to document.body so it floats above
// everything including the Bryntum scheduler.

interface OverlayPortalProps {
  resources: Resource[];
  bookings:  Booking[];
}

const OverlayPortal: React.FC<OverlayPortalProps> = ({ resources, bookings }) => {
  const { overlaySuggestion, activeSuggestions, allRisks, onApply, onCloseOverlay } = useBurnout();
  const { onOpenOverlay } = useBurnout();

  return ReactDOM.createPortal(
    <WhatIfConflictResolutionOverlay
      open={overlaySuggestion !== null}
      suggestions={activeSuggestions}
      suggestion={overlaySuggestion}
      onSelectSuggestion={onOpenOverlay}
      risks={allRisks}
      resources={resources}
      bookings={bookings}
      onClose={onCloseOverlay}
      onApply={(s) => {
        onCloseOverlay();
        onApply(s);
      }}
    />,
    document.body
  );
};

// ─── Public API ───────────────────────────────────────────────────────────────

export interface BurnoutRiskPanelWrapperProps {
  schedulerDomRef: React.RefObject<HTMLDivElement | null>;
  resources: Resource[];
  bookings: Booking[];
  globalWorkingProfile: WorkingHoursProfile | undefined;
  /** Called when Apply/Assign is clicked — navigates to calendar with scenario */
  onNavigateToCalendar: (scenario: WhatIfScenario) => void;
}

/**
 * Drop-in wrapper that renders both the alert banners (via portal) and the
 * 220 px right-side panel (in-flow, inside the flex row with the grid).
 *
 * Usage in ResourceCalendar:
 *   {isHeatmapMode && (
 *     <BurnoutRiskPanelWrapper
 *       schedulerDomRef={schedulerDomRef}
 *       resources={filteredData.filteredResources}
 *       bookings={filteredData.filteredBookings}
 *       globalWorkingProfile={settings.globalWorkingProfile}
 *     />
 *   )}
 */
export const BurnoutRiskPanelWrapper: React.FC<BurnoutRiskPanelWrapperProps> = ({
  schedulerDomRef,
  resources,
  bookings,
  globalWorkingProfile,
  onNavigateToCalendar,
}) => {
  const effectiveProfile = globalWorkingProfile ?? {
    monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8,
    saturday: 0, sunday: 0,
  };

  return (
    <BurnoutRiskProvider
      isActive
      resources={resources}
      bookings={bookings}
      globalWorkingProfile={effectiveProfile}
      onNavigateToCalendar={onNavigateToCalendar}
    >
      <BannerPortal />
      {/* BurnoutSuggestionsPanel is kept for DOM injection (badges, row tints) but renders no visible UI */}
      <BurnoutSuggestionsPanel schedulerElRef={schedulerDomRef} isActive />
      <OverlayPortal resources={resources} bookings={bookings} />
    </BurnoutRiskProvider>
  );
};
